<template>
    <div :class="[inputAttributes.classes, {'has-validation-error': error }]">
        <div class="c-input-select relative w-full h-50"
             :class="[variant, { 'hasLabelPrefix': labelPrefix, hasValue: hasValue || labelPrefix }]">
            <div v-if="labelPrefix"
                 class="c-input-select__label-prefix">
                {{ labelPrefix }}
            </div>
            <select
                v-bind="inputAttributes.other"
                :id="id"
                v-model="modelValue"
                v-prohibit-zoom
                class="c-input-select__select w-full z-0"
                :class="[selectClasses]"
                :name="id"
                @blur="onBlur"
                @focus="onFocus"
                @input="onInput">
                <slot/>
            </select>
            <CIcon name="arrow-down"
                   class="c-input-select__arrow"/>
        </div>
        <div v-if="showErrors && error"
             role="alert">
            <div :key="error"
                 class="text-red-500 text-12 font-medium px-18 pb-5 pt-5">
                {{ error }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, toRef, watch } from 'vue';
import useFieldValidation from '@/project/form/useFieldValidation';
import * as z from 'zod';

defineOptions({
    inheritAttrs: false
});

interface InputSelectProps {
    label?: string;
    name: string;
    rules?: z.Schema<any>;
    required?: boolean;
    showErrors?: boolean;
    selectClasses?: string;
    labelPrefix?: string;
    variant?: string;
}

const props = withDefaults(defineProps<InputSelectProps>(), {
    label: '',
    rules: undefined,
    required: undefined,
    showErrors: true,
    selectClasses: '',
    labelPrefix: '',
    variant: undefined,
});
const modelValue = defineModel<string | number | undefined | object>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | object | number | null | undefined | boolean): void;
    (e: 'input', value: string | object | number | null | undefined | boolean): void;
    (e: 'focus', ev: Event, valid: boolean): void;
    (e: 'blur', ev: Event, valid: boolean): void;
}>();

const constraints = computed((): z.Schema<any> => {
    if (props.required && !props.rules) {
        return z.string().nonempty().refine((val) => val.trim().length > 0, {
            message: 'Validation.required'
        });
    }
    if (props.rules) {
        return props.rules;
    }
    return z.any().optional();
});

const { field, validate, setError } = useFieldValidation(props.name, props.label, constraints, toRef(modelValue));

defineExpose({ validate, field, setError });

const attrs = useAttrs();

const inputAttributes = computed(() => {
    const { class: inheritedClass, ...inputAttributes } = attrs;
    return {
        classes: inheritedClass,
        other: inputAttributes,
    };
});

const hasFocus = ref(false);

const id = computed(() => props.name + Math.random());

const isDisabled = computed(() => {
    return !!attrs.disabled;
});

const hasValue = computed((): boolean => {
    return modelValue.value !== '' && modelValue.value != null;
});

const valid = computed(() => {
    if (!field.validated) {
        return false;
    }
    return field.valid;
});

const error = computed(() => {
    return field.error;
});

function onBlur(ev: Event) {
    hasFocus.value = false;

    if (field.dirty) {
        validate();
    }
    emit('blur', ev, valid.value);
}

function onFocus() {
    if (isDisabled.value) return;
    hasFocus.value = true;
}

function onInput(ev: Event) {
    emit('update:modelValue', modelValue.value)
}

</script>

<style lang="less" scoped>
    .c-input-select {
        @apply bg-gray-300 relative rounded-8;

        &:after {
            content: '';
            @apply absolute;
            top: 0;
            right: 0.6rem;
            bottom: 0;
            margin: auto 0;
            width: 4rem;
            height: calc(100% - 2rem);
            pointer-events: none;
        }

        .c-input-select__label {
            cursor: text;
        }
        &.hasValue .c-input-select__label {
            transform: translate(0, -100%) scale(0.8);
            padding-top: 1.9rem;
            padding-bottom: 0;
        }

        .c-input-select__select {
            @apply rounded-8 absolute inset-0 bg-transparent shadow-input pl-18 pr-25;
            -webkit-appearance: none;

            &::-ms-expand {
                display: none;
            }
            .hasValue.hasLabelPrefix& {
                @apply text-12;
                outline: 0;
                padding-top: 1.7rem;
            }

            &.invalid {
                @apply border border-solid border-red-500 text-red-500;
            }

            :deep(option) {
                @apply text-black;

                &.c-input-select__select-option-invalid,
                &[disabled] {
                    @apply cursor-default text-gray-700;
                }
            }

            &[disabled] {
                @apply cursor-default text-gray-700;
                :deep(option) {
                    @apply text-gray-700;
                    cursor: not-allowed;
                }
                + .c-input-select__arrow {
                    @apply text-gray-700;
                }
            }
        }

        .c-input--blue &,
        &.blue {
            @apply bg-blue-500 text-white;

            .c-input-select__select {
                @apply text-white;
            }
        }

        .c-input--white &,
        &.white {
            @apply bg-white text-black;

            .c-input-select__select {
                @apply text-black;
            }
        }

        .c-input-select__arrow {
            pointer-events: none;
            position: absolute;
            top: 0;
            bottom: 0;
            right: 1.6rem;
            z-index: 1;
            margin: auto 0;
            width: 1.3rem;
        }
        .c-input-select__label-prefix {
            @apply inline-block text-12 text-gray-800;
            padding-left: 1rem;
            padding-right: 1.8rem;
            padding-top: .6rem;
            transform: scale(0.8);
        }
    }

</style>
